import { create } from 'zustand';
import type { Storage } from '@chess/api-storage';

// The chess.com account settings, kept as `settings/chesscom.json` in the
// app's storage. This plugin's own state: the saved value, the draft being
// typed, and where a save stands.
export interface ChesscomSettings {
  schemaVersion: 1;
  username: string;
}

const FILE = 'chesscom.json';

export type Status = 'loading' | 'idle' | 'saving' | 'saved' | 'error';

interface State {
  username: string;
  draft: string;
  status: Status;
  error?: string;
  setDraft(draft: string): void;
  load(storage: Storage): Promise<void>;
  save(storage: Storage): Promise<void>;
}

export function parseChesscomSettings(text: string | undefined): ChesscomSettings {
  if (!text) return { schemaVersion: 1, username: '' };
  const raw = JSON.parse(text) as Partial<ChesscomSettings>;
  if (raw.schemaVersion !== 1 || typeof raw.username !== 'string') {
    throw new Error('settings/chesscom.json has an unexpected shape');
  }
  return { schemaVersion: 1, username: raw.username };
}

export function normalizeUsername(draft: string): string {
  return draft.trim();
}

export const useChesscom = create<State>((set, get) => ({
  username: '',
  draft: '',
  status: 'loading',
  setDraft: (draft) => set({ draft, status: 'idle', error: undefined }),
  async load(storage) {
    try {
      const { username } = parseChesscomSettings(await storage.readText(FILE));
      set({ username, draft: username, status: 'idle', error: undefined });
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
  },
  async save(storage) {
    const username = normalizeUsername(get().draft);
    set({ status: 'saving', error: undefined });
    try {
      const record: ChesscomSettings = { schemaVersion: 1, username };
      await storage.write(FILE, JSON.stringify(record));
      set({ username, draft: username, status: 'saved' });
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
  },
}));
